import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, Suspense, useTransition } from "react";
import { Settings } from "lucide-react";
import { pageView } from "./lib/pixel";
import { eventoGa4, itemGa4 } from "./lib/ga4";
import { registrarEvento, registrarVisita } from "./lib/metricas";
import { supabaseRT } from "./realtimeClient";
import { onForegroundMessage } from "./services/firebase";
// Só pelo efeito: o módulo registra o `beforeinstallprompt` ao carregar (o
// Chrome dispara uma vez, no início) — sem este import o "Instalar app" some.
// (05/10/2026: 78 imports SEM USO saíram daqui — entre eles o KYC facial da
// AWS, ~500 KB, que todo visitante baixava no carregamento inicial.)
import "./hooks/useInstallPrompt";
import { ToastProvider, useToast } from "./contexts/ToastContext";
import { ConfirmacaoProvider, useConfirmacao } from "./contexts/ConfirmacaoContext";
import { api } from "./api/client";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { PerfilProvider, usePerfil } from "./contexts/PerfilContext";
import { OperandoComoProvider } from "./contexts/OperandoComoContext";
import { Card, EsqueletoTela } from "./components/ui";
import { TelaAbertura, AberturaPronta } from "./components/TelaAbertura";
import { marcarAberturaPronta } from "./lib/abertura";
import { preCarregarAbas } from "./lazyScreens";
import { aplicarSeo, caminhoAnuncio, ehCaminhoVitrine, idDoSlugAnuncio, seoDaTela } from "./utils/seo";

// Telas vêm lazy-loaded de `./lazyScreens` pra code splitting por rota.
// Cada tela vira um chunk separado no bundle; só baixa quando o usuário
// navega pra ela. Reduz o bundle inicial de ~700 kB pra ~200 kB.
import {
  TelaLogin, TelaRegistro, TelaEsqueceuSenha, TelaRedefinirSenha, TelaSelecionarTipoCadastro,
  TelaTermos, TelaRegras, TelaPrivacidade, TelaExcluirConta, TelaModeracao, TelaEspecificacaoLogistica,
  TelaHome, HomeLocador, HomeFreteiro,
  TelaDetalhes, TelaDetalhesPacote, TelaBusca, TelaCarrinho, TelaCheckout, TelaFavoritos, TelaVitrine,
  TelaPedidos, TelaPedidoLocador, TelaMinhasLocacoes, TelaCentralLocacoes, TelaRotaLocador,
  TelaRastreamento, TelaPortalLocatario,
  TelaPerfil, TelaConfig, TelaDashboard, TelaVerificacao, TelaDocumentos,
  TelaMeusAnuncios, TelaInadimplencias, TelaCalendarioLocador,
  TelaExtensoesPendentes, TelaPerfilPublico, TelaMeusPacotes, TelaPerguntasLocador,
  TelaConfigLocador, TelaMeusCupons, TelaIntegracaoGestao, TelaEntregasLocador,
  TelaCompletarLote,
  TelaImportarCatalogo,
  TelaDevolucoesLocador,
  TelaFreteiroWelcome, TelaFreteiroPermissoes, TelaDashboardFreteiro,
  TelaMeusVeiculos, TelaFreteDisponivel, TelaCadastroFreteiro, TelaMeusStrikes,
  TelaFinanceiro, TelaMeusNiveis, TelaCarteiraLocador,
  TelaMinhasEmpresas, TelaCadastroLocatariaPJ, TelaCadastroEmpresa,
  TelaConvideAmigos,
  TelaAvaliacoes, TelaSuporte, TelaChat, TelaNotificacoes, TelaPublicar,
  TelaConfigAdmin, TelaDevolucoesAdmin, TelaAuditLogAdmin,
} from "./lazyScreens";
import { useFreteiroHeartbeat } from "./hooks/useFreteiroHeartbeat";
import { useFreteiroGpsEntregaAtiva } from "./hooks/useFreteiroGpsEntregaAtiva";
import { usePush } from "./hooks/usePush";
import { CATEGORIAS } from "./constants/categorias";
import { NotificacoesPedidoProvider } from "./contexts/NotificacoesPedidoContext";
import { BannerOfflineIndicator } from "./components/BannerOfflineIndicator";
import { MiniDrawerCarrinho } from "./components/MiniDrawerCarrinho";
import { TelaVistoria } from "./screens/vistoria/TelaVistoria";
import { OnboardingTour } from "./components/OnboardingTour";
import { BannerNovaVersao } from "./components/BannerNovaVersao";
// Telas de mapa com ação no rodapé: o aviso de versão não entra nelas.
const TELAS_SEM_AVISO_VERSAO = ['rota_freteiro', 'rota_locador', 'frete_disponivel', 'rastreamento', 'entregas_curso'];
import { BuildHashFooter } from "./components/BuildHashFooter";
import { BottomNav } from "./components/BottomNav";
import { Header } from "./components/Header";
import { BannerContaSuspensa } from "./components/BannerContaSuspensa";
import { ModalTelefoneObrigatorio } from "./components/ModalTelefoneObrigatorio";
import { _oauthRedirect } from "./utils/bootstrap";
import { BannerCookies } from "./components/BannerCookies";

// ─────────────────────────────────────────────────────────────────
// Roteamento URL ↔ state (History API)
// ─────────────────────────────────────────────────────────────────
// Mapa explicito de telas que tem URL "bonita" diferente do nome interno.
// Convenção pras outras: '/foo_bar' vira tela 'foo_bar' (underscore por hífen).
const URL_OVERRIDES = {
  // tela_interno: '/url'
  home: '/',
  esqueceu_senha: '/esqueceu-senha',
  redefinir_senha: '/redefinir-senha',
  selecionar_tipo_cadastro: '/cadastro',
  painel_locador: '/painel-locador',
  painel_freteiro: '/painel-freteiro',
  pedidos_locador: '/pedidos-recebidos',
  minhas_locacoes: '/locacoes',
  locacoes_central: '/central-locacoes',
  rota_locador: '/rota-entrega',
  meus_anuncios: '/anuncios',
  meus_niveis: '/niveis',
  meus_cupons: '/cupons',
  meus_pacotes: '/pacotes',
  meus_veiculos: '/veiculos',
  minhas_empresas: '/empresas',
  cadastro_freteiro: '/cadastro-freteiro',
  cadastro_locadora: '/cadastro-locadora',
  cadastro_empresa: '/cadastro-empresa',
  cadastro_locataria_pj: '/cadastro-pj',
  config: '/configuracoes',
  config_admin: '/admin',
  config_locador: '/configuracoes-locador',
  regras: '/como-funciona',
  perfil_publico: '/locador',
  detalhes_pacote: '/pacote',
  detalhes: '/equipamento',
  pedido_locador: '/pedido-recebido',
  frete_disponivel: '/fretes-disponiveis',
  entregas_curso: '/entregas',
  entregas_locador: '/entregas-recebidas',
  devolucoes_locador: '/devolucoes',
  devolucoes_admin: '/admin/devolucoes',
  audit_log_admin: '/admin/auditoria',
  admin_indicacoes: '/admin/indicacoes',
  rota_freteiro: '/rota',
  dashboard_freteiro: '/painel-freteiro-detalhado',
  freteiro_welcome: '/freteiro-bemvindo',
  freteiro_permissoes: '/freteiro-permissoes',
  portal_freteiro: '/freteiro',
  portal_locatario: '/acompanhar',
  calendario_locador: '/calendario',
  extensoes_pendentes: '/extensoes',
  perguntas_locador: '/perguntas-pendentes',
  integracao_gestao: '/integracao',
  editar_overrides_gestao: '/editar-integracao',
  completar_lote: '/completar-lote',
  importar_catalogo: '/importar-catalogo',
  meus_strikes: '/strikes',
  acompanhamento: '/acompanhamento',
  especificacao_logistica: '/logistica',
};

const _telaParaPath = (tela) => {
  if (URL_OVERRIDES[tela]) return URL_OVERRIDES[tela];
  // Default: 'foo_bar' -> '/foo-bar'
  return '/' + tela.replace(/_/g, '-');
};

// URLs antigas que hoje caem numa tela real (nada além do endereço mudou):
// '/acompanhar-pedido' era uma tela SIMULADA (timers falsos, "Betoneira 400L")
// com um botão "Cancelar Pedido — Sem cobranças" que só mudava o estado local:
// o cliente lia "Pedido Cancelado" e o pedido seguia vivo. Nenhum botão levava
// até ela; foi removida (Fase 0 do plano de cancelamento, 23/09/2026) e o
// endereço cai na Central de Locações, onde o cancelamento é o de verdade.
const URL_LEGADAS = { '/acompanhar-pedido': 'locacoes_central', '/confirmar-entrega': 'locacoes_central' };

const _pathParaTela = (path) => {
  // Remove query/hash, normaliza
  const clean = (path || '/').split('?')[0].split('#')[0].replace(/\/$/, '') || '/';
  if (URL_LEGADAS[clean]) return URL_LEGADAS[clean];
  // Vitrines públicas (SEO, 05/10/2026): /aluguel/<cidade>[/<categoria>] e /categoria/<categoria>
  if (ehCaminhoVitrine(clean)) return 'vitrine';
  // Busca no mapa reverso
  for (const [tela, url] of Object.entries(URL_OVERRIDES)) {
    if (url === clean) return tela;
    // Suporta paths com parâmetro: /equipamento/abc → tela='detalhes'
    if (url !== '/' && clean.startsWith(url + '/')) return tela;
  }
  // Default: '/foo-bar' -> 'foo_bar'
  if (clean === '/') return 'home';
  return clean.slice(1).replace(/-/g, '_').split('/')[0];
};

// Link DIRETO de produto (/equipamento/<id>): a tela vira 'detalhes', mas
// ninguém extraía o id da URL — `data` ficava null e TelaDetalhes devolvia
// vazio (link compartilhado no WhatsApp, refresh na página, "Ver anúncio").
// Devolve { id } — o mesmo shape que a home já usa em nav('detalhes', {id}),
// e a tela se completa sozinha pela API.
const _dataInicialDaUrl = (path) => {
  try {
    const tela = _pathParaTela(path);
    const clean = (path || '').split('?')[0].split('#')[0].replace(/\/$/, '');
    if (tela === 'vitrine') return { caminho: clean };
    if (tela !== 'detalhes') return null;
    const base = URL_OVERRIDES.detalhes;
    // /equipamento/<nome-cidade-uf>-<id> (SEO) ou o antigo /equipamento/<id>
    const id = clean.startsWith(base + '/') ? idDoSlugAnuncio(clean.slice(base.length + 1).split('/')[0]) : '';
    return id ? { id } : null;
  } catch { return null; }
};

const AppContent = () => {
  const { user, isLogado, loading, loginOAuth, setUser } = useAuth();
  const toastGlobal = useToast();
  const confirmarGlobal = useConfirmacao();
  const { perfilAtivo } = usePerfil();
  const push = usePush();
  // Heartbeat global: enquanto o freteiro estiver com online=true, manda
  // ping de GPS a cada 30s INDEPENDENTE da tela. Antes so rodava no
  // PortalFreteiro - se o freteiro estava na home, perfil, etc., heartbeat
  // parava e ele virava 'invisivel ao match' por GPS antigo. Backend nao
  // rebaixa mais o online (estado intencional), so filtra match por
  // heartbeat fresco (<=30min).
  useFreteiroHeartbeat(!!user?.freteiro_online);
  // GPS continuo durante entrega ativa: watchPosition em qualquer tela
  // enquanto freteiro tem entrega em status 'aceita' ou 'coletada'.
  // Atualiza entregas.lat/lng -> Supabase Realtime -> cliente ve pin
  // fluindo no mapa. Antes vivia em tela transitoria (1.2s) -> cliente
  // nunca via GPS live.
  useFreteiroGpsEntregaAtiva(!!user?.freteiro_habilitado, !!user?.locador_habilitado);
  // Tela inicial: lê da URL atual (suporta refresh em rotas internas)
  const [tela, setTela] = useState(() => {
    try { return _pathParaTela(window.location.pathname); } catch { return 'home'; }
  });
  const [data, setData] = useState(() => _dataInicialDaUrl(window.location.pathname));
  // Troca de tela SEM pausa (plano de layout, 24/09): cada tela é um chunk
  // separado e, na primeira ida, a tela atual sumia e ficava um spinner até
  // o código baixar. Dentro de startTransition o React segura a tela atual
  // no lugar; `telaCarregando` acende a barra fina laranja no topo.
  const [telaCarregando, startTransition] = useTransition();
  // Pré-baixa o código das abas da barra quando o app está ocioso.
  useEffect(() => { preCarregarAbas(); }, []);
  // Rola pro topo quando a tela NOVA entra (antes o nav() rolava a antiga,
  // que ainda estava na frente enquanto a nova baixava).
  // Instantâneo e ANTES de pintar (dono, 19/10/2026): com o `scroll-behavior:
  // smooth` global (index.css) o produto abria lá embaixo, na altura em que a
  // pessoa estava na lista, e deslizava até o topo — "carrega de baixo pra cima".
  useLayoutEffect(() => {
    const html = document.documentElement;
    const antes = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    // Lê o estilo de novo: sem isso o Chrome ainda usava o "smooth" antigo
    void getComputedStyle(html).scrollBehavior;
    try { window.scrollTo({ top: 0, left: 0, behavior: 'instant' }); } catch (e) { window.scrollTo(0, 0); }
    html.style.scrollBehavior = antes;
  }, [tela, data]);
  // Chat: a lista de conversas mostra header+abas (tela normal); só a CONVERSA
  // aberta vira fullscreen. TelaChat seta isso via onConversaAberta.
  const [chatConversaAberta, setChatConversaAberta] = useState(false);

  // Capturar ?ref=CODIGO da URL e armazenar pra usar no signup (válido 30 dias)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref');
      if (ref) {
        const codigo = ref.trim().toUpperCase().slice(0, 16);
        const expira = Date.now() + 30 * 24 * 60 * 60 * 1000;
        localStorage.setItem('toalugando_ref', codigo);
        localStorage.setItem('toalugando_ref_expira', String(expira));
        params.delete('ref');
        const novaUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
        window.history.replaceState({}, '', novaUrl);
      } else {
        // Limpa se já expirou
        const exp = parseInt(localStorage.getItem('toalugando_ref_expira') || '0', 10);
        if (exp && Date.now() > exp) {
          localStorage.removeItem('toalugando_ref');
          localStorage.removeItem('toalugando_ref_expira');
        }
      }
    } catch {}
  }, []);

  // Capturar ?convite=CODIGO — convite de LOCADOR da oferta ativa. Mesmo
  // padrão do ?ref: guarda no localStorage e o resgate acontece depois do
  // primeiro login (efeito mais abaixo), porque o cadastro exige confirmação
  // de e-mail antes de logar — o resgate imediato não teria sessão.
  useEffect(() => {
    (async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const conv = params.get('convite');
      if (conv) {
        params.delete('convite');
        const novaUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
        window.history.replaceState({}, '', novaUrl);
        const temSessao = !!localStorage.getItem('toalugando_token');
        // Já logado: pergunta antes. Sem isso, o vendedor que clicasse no
        // PRÓPRIO link pra conferir consumia o convite de uso único na conta
        // dele — e a lojista recebia "convite já usado" (cético, 11/08).
        // Modal do app em vez do window.confirm do navegador (24/09).
        if (temSessao) {
          const ok = await confirmarGlobal({
            titulo: 'Aplicar convite de locador?',
            mensagem: 'Este é um convite de LOCADOR. Aplicar nesta conta que está logada? Se o convite é para outra pessoa, toque em Cancelar — o link continua válido para ela.',
            confirmTexto: 'Aplicar',
            cancelarTexto: 'Cancelar',
          });
          if (!ok) return;
        }
        localStorage.setItem('toalugando_convite_locador', conv.trim().toUpperCase().slice(0, 32));
        // Visitante com convite cai direto no cadastro — é o fluxo da loja:
        // recebeu o link no WhatsApp, abriu, cria a conta na hora.
        if (!temSessao) setTela('registro');
      }
    } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resgate do convite de locador após o login: consome o código no backend
  // (que liga locador_habilitado + horário padrão) e atualiza o user local.
  const convResgatandoRef = useRef(false);
  useEffect(() => {
    if (!isLogado || !user?.id) return;
    const codigo = (() => { try { return localStorage.getItem('toalugando_convite_locador'); } catch { return null; } })();
    if (!codigo || convResgatandoRef.current) return;
    convResgatandoRef.current = true;
    (async () => {
      try {
        const r = await api.fetch(`/convites-locador/${encodeURIComponent(codigo)}/usar`, { method: 'POST' });
        const j = await r.json().catch(() => ({}));
        if (r.ok) {
          try { localStorage.removeItem('toalugando_convite_locador'); } catch {}
          // Recarrega o perfil pra UI enxergar locador_habilitado sem relogin
          try {
            const ru = await api.fetch('/usuarios/me');
            if (ru.ok) { const u = await ru.json(); if (u && setUser) setUser(u); }
          } catch {}
          toastGlobal.success(j?.mensagem || 'Conta liberada! Você já pode anunciar.');
        } else if (r.status === 409) {
          // Convite usado/expirado/revogado: informa uma vez e descarta o código
          try { localStorage.removeItem('toalugando_convite_locador'); } catch {}
          toastGlobal.error(j?.detail || 'Este convite não é mais válido.');
        }
        // Erro de rede/5xx: mantém o código guardado e tenta no próximo load.
      } catch {}
      finally { convResgatandoRef.current = false; }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogado, user?.id]);

  // Capturar ?tela= da URL — usado quando service worker abre o app via push
  // ex: /?tela=portal_freteiro&pedido=ID
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const telaQS = params.get('tela');
      if (telaQS && isLogado) {
        setTela(telaQS);
        // mantém ?pedido= se houver — a tela destino vai consumir
        params.delete('tela');
        const novaUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
        window.history.replaceState({}, '', novaUrl);
      }
    } catch {}
  }, [isLogado]);

  // ── Listener: locador clicou "Estou analisando (+5min)" no popup de notificação ──
  // Service worker manda postMessage OU navegador abriu o app com ?adiar_pedido=ID.
  useEffect(() => {
    const adiarPedido = async (pedidoId) => {
      if (!pedidoId) return;
      try {
        await api.fetch(`/pedidos/${pedidoId}/adiar-alerta?minutos=5`, { method: 'POST' });
      } catch {}
    };

    const onSwMsg = (e) => {
      if (e.data?.tipo === 'adiar_alerta_pedido') adiarPedido(e.data.pedido_id);
    };
    if (navigator.serviceWorker) navigator.serviceWorker.addEventListener('message', onSwMsg);

    // Caso o SW abriu o app com query string (sem postMessage anterior)
    try {
      const params = new URLSearchParams(window.location.search);
      const pid = params.get('adiar_pedido');
      if (pid) {
        adiarPedido(pid);
        params.delete('adiar_pedido');
        const novaUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
        window.history.replaceState({}, '', novaUrl);
      }
    } catch {}

    return () => {
      if (navigator.serviceWorker) navigator.serviceWorker.removeEventListener('message', onSwMsg);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carrinho com persistência POR USUÁRIO (Strategy B):
  // - Logado:    chave `toalugando_cart_<user_id>` — cada user tem o próprio bucket
  // - Anônimo:   chave `toalugando_cart_anon`     — visitante mantém entre sessões
  // No logout NÃO apaga (carrinho fica vinculado à conta, retorna ao logar de novo).
  const _cartKey = (uid) => uid ? `toalugando_cart_${uid}` : 'toalugando_cart_anon';
  const cartKeyAtual = _cartKey(user?.id);
  const [cart, setCart] = useState(() => {
    try {
      // Tenta primeiro a chave do user atual; fallback pra chave anon (caso o user acabou de logar com carrinho local)
      const raw = localStorage.getItem(_cartKey(null)) // anon primeiro só na primeira inicialização
        || (typeof window !== 'undefined' && window.__cartInit) // placeholder
        || null;
      const r2 = localStorage.getItem(cartKeyAtual);
      const escolhido = r2 || raw;
      return escolhido ? JSON.parse(escolhido) : [];
    } catch { return []; }
  });

  // Quando o user troca (login/logout), recarrega o carrinho da chave nova
  useEffect(() => {
    try {
      const raw = localStorage.getItem(cartKeyAtual);
      // Anônimo entrando logado: faz merge do carrinho anon no carrinho do user (uma vez)
      if (user?.id) {
        const rawAnon = localStorage.getItem(_cartKey(null));
        if (rawAnon) {
          const anonItens = JSON.parse(rawAnon);
          const userItens = raw ? JSON.parse(raw) : [];
          // Merge: items do anon não duplicados (por id) entram no carrinho do user
          const ids = new Set(userItens.map(i => i.id));
          const merged = [...userItens, ...anonItens.filter(i => !ids.has(i.id))];
          setCart(merged);
          localStorage.removeItem(_cartKey(null)); // limpa anon depois do merge
          return;
        }
      }
      setCart(raw ? JSON.parse(raw) : []);
    } catch { setCart([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Sincroniza qualquer mudança no carrinho com a chave por usuário
  useEffect(() => {
    try {
      if (cart.length === 0) {
        localStorage.removeItem(cartKeyAtual);
      } else {
        localStorage.setItem(cartKeyAtual, JSON.stringify(cart));
      }
    } catch (e) {
      console.warn('[Cart] não persistiu:', e?.message);
    }
  }, [cart, cartKeyAtual]);
  const [itemAdicionadoCarrinho, setItemAdicionadoCarrinho] = useState(null);
  const [notifs, setNotifs] = useState([]);
  // Contador de CHAT — SEPARADO das notificações do sino (pedido do dono):
  // o badge da aba Chat mostrava "27" vindas do sino sem nenhuma conversa.
  const [chatNaoLidas, setChatNaoLidas] = useState({ mensagens: 0, conversas: 0 });
  // Banner clicável de mensagem nova (estilo WhatsApp) — independente do FCM.
  const [bannerChat, setBannerChat] = useState(null); // {titulo, corpo}
  const [favs, setFavs] = useState(new Set());
  const [oauthCarregando, setOauthCarregando] = useState(!!(_oauthRedirect && _oauthRedirect.type !== 'recovery'));

  // Telas que NÃO entram no histórico (não faz sentido "voltar" para elas)
  const TELAS_SEM_HISTORICO = new Set(['login', 'registro', 'esqueceu_senha', 'redefinir_senha', 'selecionar_tipo_cadastro']);

  const nav = useCallback((t, d = null) => {
    // 'back' = volta para a tela anterior no histórico (com fallback para home)
    if (t === 'back') {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        startTransition(() => { setTela('home'); setData(null); });
        window.history.pushState({ tela: 'home', data: null }, '', '/');
      }
      return;
    }
    startTransition(() => { setTela(t); setData(d); });
    if (!TELAS_SEM_HISTORICO.has(t)) {
      // Monta path com base na tela + id quando aplicável (ex: /equipamento/123)
      let path = _telaParaPath(t);
      const id = d && (d.id || d.equipamento_id || d.pedido_id || d.locador_id);
      if (t === 'vitrine' && d?.caminho) path = d.caminho;
      // Anúncio: URL canônica vinda da vitrine, ou legível a partir do nome
      // (a TelaDetalhes ainda confirma a canônica no servidor)
      else if (t === 'detalhes' && d?._caminho) path = d._caminho;
      else if (t === 'detalhes' && d?.id && d?.nome) path = caminhoAnuncio(d);
      else if (id && path !== '/') path = `${path}/${id}`;
      // pushState só se a URL muda (evita poluir o histórico)
      if (path !== window.location.pathname) {
        window.history.pushState({ tela: t, data: d }, '', path);
      } else {
        window.history.replaceState({ tela: t, data: d }, '', path);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listener do botão Voltar/Avançar do navegador
  useEffect(() => {
    // Registra a tela inicial no histórico (preserva URL atual)
    const telaInicial = _pathParaTela(window.location.pathname);
    // Preserva ?query e #hash: regravar só o pathname apagava o ?tela=/?aba=/
    // ?pedido= antes do login terminar de carregar — todo link de push e de
    // e-mail caía na Home, e /admin?aba=... abria no Painel (auditoria 29/10, A8-01).
    window.history.replaceState({ tela: telaInicial, data: _dataInicialDaUrl(window.location.pathname) }, '',
      window.location.pathname + window.location.search + window.location.hash);

    const handlePopState = (e) => {
      // Prioriza state armazenado (data preservado); se não, deriva tela da URL
      // (e o id do produto, quando a URL é /equipamento/<id>)
      startTransition(() => {
        if (e.state && e.state.tela) {
          setTela(e.state.tela);
          setData(e.state.data || _dataInicialDaUrl(window.location.pathname));
        } else {
          setTela(_pathParaTela(window.location.pathname));
          setData(_dataInicialDaUrl(window.location.pathname));
        }
      });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Meta Pixel — PageView por ROTA. Esta SPA troca de tela sem recarregar a
  // pagina, entao o pixel do index.html dispararia UMA unica visita por
  // sessao inteira. Depender de `tela` cobre os dois caminhos de navegacao:
  // o nav() (pushState) e o botao Voltar do navegador (popstate) — ambos
  // terminam em setTela.
  // O primeiro render e pulado de proposito: o index.html ja mandou a
  // PageView inicial, e sem isso toda visita entraria em dobro.
  // Dentro do app nativo nada dispara — pixel.js checa o hostname.
  // Painel do Portal de Gestão (08/10): uma visita por sessão, site e app
  useEffect(() => { registrarVisita(); }, []);
  const pixelPrimeiroRender = useRef(true);
  useEffect(() => {
    if (pixelPrimeiroRender.current) {
      pixelPrimeiroRender.current = false;
      return;
    }
    pageView();
  }, [tela]);

  // SEO por tela (05/10/2026): anúncio e vitrine cuidam do próprio título e
  // canonical (dados do servidor); o resto volta ao padrão — área logada com
  // noindex, telas públicas com canonical.
  useEffect(() => {
    if (tela === 'detalhes' || tela === 'vitrine') return;
    aplicarSeo(seoDaTela(tela));
  }, [tela]);

  // Processar redirect do Supabase (OAuth ou reset de senha)
  useEffect(() => {
    // Query param: ?tela=redefinir_senha&token=...
    const params = new URLSearchParams(window.location.search);
    if (params.get('tela') === 'redefinir_senha' && params.get('token')) {
      window.history.replaceState({}, '', window.location.pathname);
      setTela('redefinir_senha');
      setData({ token: params.get('token') });
      return;
    }
    // Hash do Supabase (já lido no módulo — não depende de timing do React)
    if (_oauthRedirect) {
      const { accessToken, refreshToken, type } = _oauthRedirect;
      if (type === 'recovery') {
        setTela('redefinir_senha');
        setData({ token: accessToken });
      } else {
        loginOAuth(accessToken, refreshToken)
          .then(() => {
            setOauthCarregando(false);
            // Onboarding: a antiga tela "como você vai usar" (locatário/
            // locadora/freteiro) foi REMOVIDA — era confusa. Todo mundo entra
            // direto como locatário (o padrão já criado no backend) e ativa
            // Locador/Freteiro dentro do Perfil ("Ativar perfil Freteiro",
            // KYC) ou pelo botão "Anunciar". Novo e recorrente vão pra home.
            setTela('home');
          })
          .catch((e) => {
            setOauthCarregando(false);
            // 2FA ligado: a tela de login pede o código do app autenticador
            if (e.mfa) { setTela('login'); setData({ mfa: e.mfa }); return; }
            console.error('[OAuth] Erro:', e.message);
            const msg = e.message === 'Failed to fetch'
              ? 'Não foi possível conectar ao servidor. Verifique sua internet e tente novamente.'
              : ('Erro ao entrar com Google: ' + e.message);
            toastGlobal.erro(msg);
          });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const addCart = (item, filhos = []) => {
    // Visitante deslogado precisa logar antes de criar carrinho/reservar
    if (!isLogado) { setTela('login'); return; }
    // Se já existe item com o mesmo id (equipamento) no carrinho, ATUALIZA em vez
    // de duplicar — usuário voltou pra editar dias/frete/etc do mesmo produto.
    // `filhos` = componentes grátis + acessórios vinculados escolhidos (modelo
    // do Gestão): entram como linhas próprias marcadas com vinculoPaiId, e são
    // SUBSTITUÍDOS por inteiro a cada atualização do pai.
    let foiAtualizacao = false;
    setCart(p => {
      const semFilhosAntigos = p.filter(it => it.vinculoPaiId !== item.id);
      const idx = semFilhosAntigos.findIndex(it => it.id === item.id && !it.vinculoPaiId);
      if (idx >= 0) {
        foiAtualizacao = true;
        const novo = [...semFilhosAntigos];
        novo[idx] = { ...item };
        return [...novo, ...filhos];
      }
      return [...semFilhosAntigos, item, ...filhos];
    });
    if (foiAtualizacao) {
      // Atualizacao do mesmo item: toast simples basta
      toastGlobal.info('Pedido atualizado');
    } else {
      // Item novo: abre mini-drawer ML-style com CTA pro carrinho
      setItemAdicionadoCarrinho(item);
      // Funil do GA4 (08/10): adicionou ao carrinho — valor só da locação
      const valorItem = Number(item.subtotal) || 0;
      eventoGa4('add_to_cart', { currency: 'BRL', value: Number(valorItem.toFixed(2)), items: [itemGa4(item)] });
      registrarEvento('carrinho_adicionado', { equipamento_id: item.id, valor: valorItem });
    }
  };
  const toggleFav = id => {
    if (!isLogado) { setTela('login'); return; }
    // Otimista, mas DESFAZ se o servidor recusar — antes a falha era engolida
    // e o coração ficava marcado sem nada salvo (auditoria 29/10, A5-21). A
    // chamada saiu de dentro do setFavs (o React pode rodar o updater 2×).
    const tinha = favs.has(id);
    const aplicar = (marcado) => setFavs(p => {
      const n = new Set(p);
      if (marcado) n.add(id); else n.delete(id);
      return n;
    });
    aplicar(!tinha);
    api.fetch(`/usuarios/me/favoritos/${id}`, { method: tinha ? 'DELETE' : 'POST' })
      .then(r => { if (!r.ok) throw new Error('favorito'); })
      .catch(() => { aplicar(tinha); toastGlobal.erro('Não foi possível salvar o favorito. Tente de novo.'); });
  };

  // Favoritos persistentes: carrega do backend ao logar (cross-device); limpa ao sair.
  useEffect(() => {
    if (!isLogado) { setFavs(new Set()); return; }
    let cancelado = false;
    api.fetch('/usuarios/me/favoritos')
      .then(r => (r.ok ? r.json() : []))
      .then(ids => { if (!cancelado && Array.isArray(ids)) setFavs(new Set(ids)); })
      .catch(() => {});
    return () => { cancelado = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogado]);

  // Ao fazer logout, redireciona pra home (visitante navega livre) e limpa estado.
  // IMPORTANTE: só redireciona em LOGOUT real (transição logado→deslogado), não
  // na primeira visita do user — visitante deve cair na home, não na tela de login.
  const eraLogadoRef = useRef(false);
  useEffect(() => {
    if (loading) return;
    if (eraLogadoRef.current && !isLogado) {
      // Logout real
      setNotifs([]);
      nav('home');
    }
    eraLogadoRef.current = isLogado;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogado, loading]);

  // Registrar push silenciosamente após login (se permissão já foi concedida antes)
  useEffect(() => {
    if (!isLogado) return;
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && !push.token) {
      push.solicitarPermissao().catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogado]);

  // Carrega lista de notificações ao logar + a cada 60s (polling leve) + Realtime.
  // Sem isso, o badge do sino fica sempre em 0 até o user abrir /notificacoes.
  // Push FCM dispara toast/popup mas nao atualiza essa lista.
  // Realtime (enhancement): quando chega/lê notificação em QUALQUER aparelho, o
  // sino sincroniza ao vivo (<1s) em todos os devices — não espera o poll de 60s.
  useEffect(() => {
    if (!isLogado) { setNotifs([]); return; }
    let cancelado = false;
    const carregar = async () => {
      try {
        const data = await api.listarNotificacoes();
        const lista = Array.isArray(data) ? data : (data?.items || []);
        if (!cancelado) setNotifs(lista);
      } catch (e) {
        // silencioso — sino mostra 0 se backend indisponivel
      }
    };
    carregar();
    const intervalo = setInterval(carregar, 60000);

    // BLINDAGEM: Realtime é ENHANCEMENT — o poll acima é o caminho garantido.
    // Qualquer erro do supabase-js aqui NÃO pode derrubar o app.
    let canal = null;
    const uid = user?.id;
    if (uid) {
      try {
        canal = supabaseRT.channel(`sino_${uid}_${Math.floor(Math.random() * 1e6)}`);
        canal.on(
          "postgres_changes",
          { event: "*", schema: "public", table: "notificacoes", filter: `usuario_id=eq.${uid}` },
          () => { if (!cancelado) carregar(); }
        );
        canal.subscribe();
      } catch (e) {
        try { if (canal) supabaseRT.removeChannel(canal); } catch {}
        canal = null;
      }
    }

    return () => {
      cancelado = true;
      clearInterval(intervalo);
      try { if (canal) supabaseRT.removeChannel(canal); } catch {}
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogado]);

  // ── CHAT: contador próprio + banner de mensagem nova (estilo WhatsApp) ──
  // Independente do FCM (que exige permissão de notificação): assina o
  // Realtime da tabela `mensagens` filtrado por MIM como destinatário — a
  // mesma infra que a conversa aberta já usa. Fora da tela de chat, mensagem
  // nova vira banner clicável + badge; dentro dela, a TelaChat cuida.
  const telaRef = useRef(tela);
  useEffect(() => { telaRef.current = tela; });
  useEffect(() => {
    if (!isLogado) { setChatNaoLidas({ mensagens: 0, conversas: 0 }); return; }
    let cancelado = false;
    const atualizar = async () => {
      const c = await api.chatNaoLidas();
      if (!cancelado) setChatNaoLidas(c);
    };
    atualizar();
    const intervalo = setInterval(atualizar, 30000);
    // A TelaChat avisa quando o usuário leu (abrir a conversa marca lida no
    // servidor) — o badge zera na hora, sem esperar o próximo poll.
    window.addEventListener('toalugando:chat:lido', atualizar);

    let canal = null;
    const uid = user?.id;
    if (uid) {
      try {
        canal = supabaseRT.channel(`chat_in_${uid}_${Math.floor(Math.random() * 1e6)}`);
        canal.on(
          "postgres_changes",
          { event: "INSERT", schema: "public", table: "mensagens", filter: `destinatario_id=eq.${uid}` },
          (payload) => {
            if (cancelado) return;
            atualizar();
            // Banner só FORA do chat — lá dentro a mensagem já aparece na hora.
            if (telaRef.current === 'chat') return;
            const txt = String(payload?.new?.conteudo || 'Você recebeu uma mensagem');
            const remetenteId = payload?.new?.remetente_id || null;
            // Mostra QUEM mandou (foto + nome), não só o texto solto (pedido do
            // dono, 05/08). O evento do Realtime traz só os ids, então o nome
            // vem da lista de conversas — que já tem outro_usuario.
            setBannerChat({
              corpo: txt.length > 90 ? txt.slice(0, 90) + '…' : txt,
              remetenteId,
              nome: null,
              foto: null,
            });
            if (remetenteId) {
              api.listarConversas()
                .then(convs => {
                  const c = (Array.isArray(convs) ? convs : [])
                    .find(x => x?.outro_usuario?.id === remetenteId);
                  const u = c?.outro_usuario;
                  if (u) setBannerChat(prev => (prev && prev.remetenteId === remetenteId)
                    ? { ...prev, nome: u.nome || null, foto: u.foto_url || null }
                    : prev);
                })
                .catch(() => {});
            }
            setTimeout(() => setBannerChat(null), 6000);
          }
        );
        canal.subscribe();
      } catch (e) {
        try { if (canal) supabaseRT.removeChannel(canal); } catch {}
        canal = null;
      }
    }
    return () => {
      cancelado = true;
      clearInterval(intervalo);
      window.removeEventListener('toalugando:chat:lido', atualizar);
      try { if (canal) supabaseRT.removeChannel(canal); } catch {}
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogado, user?.id]);

  // Ouvir notificações push em foreground (aba ativa) — Firebase NÃO mostra
  // notificação nativa quando aba está em foco; nós mostramos manualmente
  // via service worker pra ter som + popup do SO.
  useEffect(() => {
    if (!isLogado) return;
    const unsubscribe = onForegroundMessage(async (payload) => {
      const data = payload.data || {};
      // Backend manda data-only payload — title/body vêm em data
      const title = data.title || payload.notification?.title || 'Tô Alugando';
      const body = data.body || payload.notification?.body || 'Você tem uma nova notificação';

      // Mensagem de CHAT em foreground: o banner do Realtime (acima) já cobre
      // — sem este return o usuário via DOIS avisos da mesma mensagem. Só
      // atualiza o badge e sai.
      if (data.type === 'chat_mensagem') {
        try { setChatNaoLidas(await api.chatNaoLidas()); } catch {}
        return;
      }

      // 1. Toast in-app
      toastGlobal.info(`${title}: ${body}`);

      // 1b. Recarrega lista de notifs pra atualizar badge do sino + tela
      try {
        const data = await api.listarNotificacoes();
        const lista = Array.isArray(data) ? data : (data?.items || []);
        setNotifs(lista);
      } catch (e) {}

      // 2. Notificação nativa via SW — toca som do SO + aparece popup
      try {
        if ('Notification' in window && Notification.permission === 'granted') {
          const reg = await navigator.serviceWorker.ready;
          const isAlertaTimer = data.type === 'alerta_timer';
          await reg.showNotification(title, {
            body,
            icon: '/icon-192x192.png',
            badge: '/badge-72x72.png',
            tag: data.pedido_id || data.type || 'toalugando-fg',
            renotify: true,
            requireInteraction: false,
            silent: false,
            vibrate: [300, 100, 300, 100, 300],
            data,
            actions: isAlertaTimer
              ? [
                  { action: 'open', title: 'Abrir' },
                  { action: 'adiar', title: 'Estou analisando (+5min)' },
                ]
              : [
                  { action: 'open', title: 'Abrir' },
                  { action: 'dismiss', title: 'Dispensar' },
                ],
          });
        }
      } catch (e) { /* silencioso */ }
    });
    return () => { if (typeof unsubscribe === 'function') unsubscribe(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogado]);

  // Redirecionar entre painéis ao trocar de perfil. `home` é UNIVERSAL —
  // é o catálogo público, qualquer perfil pode ficar nele. Só redirecionamos
  // se o usuário estiver num painel ESPECÍFICO de outro perfil (ex.: locatário
  // entrou em painel_locador por engano via URL).
  useEffect(() => {
    if (!isLogado) return;
    if (['login','registro','esqueceu_senha','redefinir_senha'].includes(tela)) return;
    const ehPainelDeLocador  = tela === 'painel_locador';
    const ehPainelDeFreteiro = tela === 'painel_freteiro' || tela === 'freteiro_welcome';
    if (ehPainelDeLocador  && perfilAtivo !== 'locador')  setTela('home');
    if (ehPainelDeFreteiro && perfilAtivo !== 'freteiro') setTela('home');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perfilAtivo, isLogado]);

  // Telas do admin abertas por URL por quem não é admin: volta pra home. O
  // servidor já recusa os dados, mas a tela abria vazia/quebrada (A8-14).
  useEffect(() => {
    const ehTelaAdmin = ['config_admin', 'audit_log_admin', 'devolucoes', 'devolucoes_admin'].includes(tela);
    if (ehTelaAdmin && user && !(user.is_admin || user.tipo_ativo === 'admin')) setTela('home');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tela, user]);

  // Proteger rotas que precisam de login
  const protectedNav = useCallback((t, d = null) => {
    const rotasProtegidas = ['carrinho', 'checkout', 'pedidos', 'publicar', 'perfil', 'dashboard', 'meus_anuncios', 'meus_veiculos', 'meus_niveis', 'config', 'painel_freteiro', 'favoritos', 'avaliacoes', 'suporte', 'carteira', 'carteira_locador', 'convide', 'verificacao', 'cadastro_freteiro', 'cadastro_locadora', 'devolucoes', 'devolucoes_admin', 'precificacao', 'completar_lote', 'importar_catalogo', 'devolucoes_locador', 'financeiro', 'inadimplencias', 'minhas_locacoes', 'config_admin', 'audit_log_admin', 'pedidos_locador', 'frete_disponivel', 'painel_locador', 'historico_ganhos', 'entregas_curso', 'vistoria', 'config_loja', 'chat', 'notificacoes'];
    // perfil_publico não é protegido — é leitura pública para construir confiança
    if (rotasProtegidas.includes(t) && !isLogado) {
      setTela('login');
      return;
    }
    nav(t, d);
  }, [isLogado, nav]);

  // 'painel_freteiro' entra como fullscreen: o hub do freteiro é um mapa em
  // tela cheia estilo Uber — o Header do marketplace não faz parte dele.
  // (BottomNav continua: gate separado na linha do BottomNav.)
  // CHAT: a LISTA de conversas é tela normal (header + abas de baixo, como as
  // outras); só a CONVERSA ABERTA vira fullscreen (layout 100dvh do teclado).
  // TelaChat sinaliza via onConversaAberta.
  const fullscreen = (tela === 'chat')
    ? chatConversaAberta
    : ['rastreamento', 'rota_locador', 'login', 'registro', 'esqueceu_senha', 'redefinir_senha', 'frete_disponivel', 'portal_freteiro', 'painel_freteiro', 'rota_freteiro', 'entregas_curso', 'portal_locatario'].includes(tela);
  const authScreen = ['login', 'registro', 'esqueceu_senha', 'redefinir_senha'].includes(tela);

  // Login sendo conferido / volta do "Entrar com Google": a abertura gêmea da
  // do index.html (mesmo desenho, setas girando). No retorno do Google a
  // abertura do HTML sai na hora para o aviso "Entrando com Google..." aparecer.
  if (loading || oauthCarregando) {
    if (oauthCarregando) marcarAberturaPronta();
    return <TelaAbertura aviso={oauthCarregando ? 'Entrando com Google...' : null} />;
  }

  return (
    <NotificacoesPedidoProvider perfilAtivo={perfilAtivo} nav={protectedNav} user={user} tela={tela}>
      {/* Telas fullscreen (chat 100dvh, hub do freteiro) controlam o próprio
          rodapé — o pb-14 global criava 56px de rolagem fantasma nelas */}
      {/* paddingTop nas telas FULLSCREEN: elas não têm o Header global (que já
          reserva a área segura), então no app instalado o relógio/bateria do
          celular cobria o próprio cabeçalho delas — "Acompanhando pedido",
          rastreio, hub do freteiro etc. (relato do dono, 05/08). Uma correção
          aqui vale pra TODAS. No navegador o inset é 0 e nada muda. */}
      <div
        style={{
          // Roboto = a fonte do Android, carregada pra todo mundo no index.html.
          // A 'DM Sans' que morava aqui deixava o resultado ao acaso da
          // plataforma — no Windows sobrava a Segoe UI, que desenha os pesos
          // 600/800 quase como negrito (o site inteiro parecia em bold no PC).
          fontFamily: "'Roboto','Segoe UI',system-ui,sans-serif",
          // O CHAT fica de fora: ele calcula a própria altura (100dvh) por
          // causa do teclado, e um padding aqui criaria rolagem fantasma.
          ...(fullscreen && tela !== 'chat' ? { paddingTop: 'env(safe-area-inset-top, 0px)' } : {}),
        }}
        className={!authScreen && !fullscreen ? "pb-14 md:pb-0" : ""}
      >
        {/* Barra fina de progresso no topo enquanto a próxima tela baixa
            (estilo iFood). Some sozinha quando a tela nova entra. */}
        {telaCarregando && (
          <div className="fixed top-0 left-0 right-0 z-[9998] h-[3px] pointer-events-none" role="progressbar" aria-label="Carregando a tela">
            <div className="h-full bg-orange-500 rounded-r-full animate-[barraProgresso_1.4s_ease-out_forwards]" />
          </div>
        )}
        {/* Na HOME o Header global fica oculto: o topo navy do redesign é o
            cabeçalho (busca + sino + carrinho + localização). */}
        {!fullscreen && tela !== 'home' && <Header tela={tela} nav={protectedNav} cart={cart} notifs={notifs} />}
        {/* Portão OBRIGATÓRIO de telefone: logado sem número CONFIRMADO
            (entrou pelo Google, que pula o formulário; ou digitou um número no
            cadastro que ainda não provou ser dele) → bloqueia o app até
            confirmar por código no WhatsApp. É o contato dos avisos de
            pedido/pagamento/entrega. Quando o backend está com a exigência
            desligada, ele já devolve telefone_verificado=true ao salvar, então
            este portão não entra em laço. */}
        {isLogado && user?.id && !authScreen
          && (!String(user?.telefone || '').trim() || user?.telefone_verificado === false)
          && <ModalTelefoneObrigatorio />}
        {!fullscreen && user?.suspenso_em && <BannerContaSuspensa />}
        <BannerOfflineIndicator />
        {/* Aviso de nova versão: fora das telas de mapa/ação (cobria o
            "ARRASTE PARA INICIAR" do freteiro, 25/09) e do app nativo. */}
        <BannerNovaVersao oculto={TELAS_SEM_AVISO_VERSAO.includes(tela)} semNav={fullscreen} />
        {/* Consentimento de cookies — só no site; Pixel e GA4 só depois do aceite (LGPD, 24/10) */}
        {!authScreen && <BannerCookies nav={protectedNav} semBarra={fullscreen} />}
        {/* O banner automático "Instale o Tô Alugando" saiu (24/09): instalar
            virou item do menu lateral e de Configurações (InstalarApp.js). */}
        {/* Tour direcionado pela INTENCAO do signup: quem se registrou como
            FRETEIRO/LOCADORA ve o tour certo na primeira visita, mesmo que o
            perfilAtivo ainda seja 'locatario' (default pre-habilitacao). */}
        {isLogado && user?.id && !authScreen && (
          <OnboardingTour
            perfil={
              user.tipo_signup === 'FRETEIRO' && perfilAtivo === 'locatario' ? 'freteiro' :
              user.tipo_signup === 'LOCADORA_BASICA' && perfilAtivo === 'locatario' ? 'locador' :
              perfilAtivo
            }
            userId={user.id}
          />
        )}
        
        {/* Telas com code splitting — Suspense exibe fallback enquanto baixa o chunk da rota */}
        {/* Só aparece na PRIMEIRA carga (sem tela anterior). Nas trocas de
            tela o nav() usa startTransition: a tela atual fica no lugar e a
            barra fina laranja no topo mostra que a próxima está chegando. */}
        <Suspense fallback={<EsqueletoTela />}>
        {/* Avisa a abertura (logo) quando o código desta tela já baixou — ver lib/abertura.js */}
        <AberturaPronta tela={tela} />
        {tela === 'login' && <TelaLogin nav={nav} mfaInicial={data?.mfa} />}
        {tela === 'registro' && <TelaRegistro nav={nav} />}
        {/* /cadastro existia no mapa de URLs e no lazyScreens, mas sem este
            branch a página abria EM BRANCO (auditoria 04/08). */}
        {tela === 'selecionar_tipo_cadastro' && <TelaSelecionarTipoCadastro nav={nav} />}
        {tela === 'esqueceu_senha' && <TelaEsqueceuSenha nav={nav} />}
        {tela === 'redefinir_senha' && <TelaRedefinirSenha nav={nav} data={data} />}
        {/* "Início" é UNIVERSAL = catálogo, pra TODOS os perfis. O locador NÃO cai
            mais no painel ao abrir o app — as abas de baixo são idênticas às do
            locatário; o que muda pro locador fica no menu (drawer) e em Configurações.
            O Painel do Locador segue acessível pelo menu "Painel do Locador" e pela
            rota 'painel_locador' abaixo (nada foi removido). */}
        {tela === 'home' && <TelaHome nav={protectedNav} favs={favs} toggleFav={toggleFav} data={data} notifs={notifs} cart={cart} />}
        {tela === 'painel_locador' && <HomeLocador nav={protectedNav} user={user} />}
        {tela === 'painel_freteiro' && <HomeFreteiro nav={protectedNav} user={user} data={data} />}
        
        {/* Telas Compartilhadas */}
        {tela === 'detalhes' && <TelaDetalhes key={data?.id || 'detalhes'} item={data} nav={protectedNav} addCart={addCart} cart={cart} />}
        {tela === 'vitrine' && <TelaVitrine key={data?.caminho} data={data} nav={protectedNav} />}
        {tela === 'busca' && <TelaBusca nav={protectedNav} initQ={data?.q} initLocador={data?.locadorId ? { id: data.locadorId, nome: data.locador } : null} />}
        {tela === 'carrinho' && <TelaCarrinho cart={cart} setCart={setCart} nav={protectedNav} />}
        {tela === 'publicar' && <TelaPublicar nav={protectedNav} itemEditar={data?.editar || null} />}
        {tela === 'regras' && <TelaRegras nav={protectedNav} />}
        {tela === 'termos' && <TelaTermos nav={protectedNav} />}
        {tela === 'privacidade' && <TelaPrivacidade nav={protectedNav} />}
        {/* /excluir-conta: página pública exigida pelo Google Play (Data safety) */}
        {tela === 'excluir_conta' && <TelaExcluirConta nav={protectedNav} />}
        {tela === 'moderacao' && <TelaModeracao nav={protectedNav} />}
        {tela === 'especificacao_logistica' && <TelaEspecificacaoLogistica nav={protectedNav} />}
        {/* onPedidosCriados: o checkout não tinha acesso ao carrinho, então
            depois de "Pedido enviado!" o badge continuava cheio e um novo
            "Solicitar Locação" criava pedidos DUPLICADOS (auditoria 04/08). */}
        {tela === 'checkout' && <TelaCheckout data={data} nav={protectedNav} onPedidosCriados={() => setCart([])} />}
        {tela === 'pedidos' && <TelaPedidos nav={protectedNav} data={data} />}
        {tela === 'chat' && <TelaChat nav={protectedNav} data={data} user={user} onConversaAberta={setChatConversaAberta} />}
        {tela === 'notificacoes' && <TelaNotificacoes notifs={notifs} setNotifs={setNotifs} nav={protectedNav} />}
        {tela === 'perfil' && <TelaPerfil nav={protectedNav} data={data} />}
        {tela === 'dashboard' && <TelaDashboard nav={protectedNav} />}
        {tela === 'cadastro_freteiro' && <TelaCadastroFreteiro nav={protectedNav} />}
        {/* cadastro_locadora — deprecated, redirect pra cadastro_empresa */}
        {tela === 'cadastro_locadora' && <TelaCadastroEmpresa nav={protectedNav} data={data} />}
        {tela === 'cadastro_locataria_pj' && <TelaCadastroLocatariaPJ nav={protectedNav} />}
        {tela === 'minhas_empresas' && <TelaMinhasEmpresas nav={protectedNav} />}
        {tela === 'cadastro_empresa' && <TelaCadastroEmpresa nav={protectedNav} data={data} />}
        {tela === 'avaliacoes' && <TelaAvaliacoes nav={protectedNav} />}
        {tela === 'rastreamento' && <TelaRastreamento data={data} nav={protectedNav} />}
        {tela === 'portal_locatario' && <TelaPortalLocatario data={data} nav={protectedNav} />}
        {tela === 'meus_anuncios' && <TelaMeusAnuncios nav={protectedNav} data={data} />}
        {tela === 'config' && <TelaConfig nav={protectedNav} data={data} />}
        {/* toggleFav: sem ele não dava pra desfavoritar de dentro da tela */}
        {tela === 'favoritos' && <TelaFavoritos nav={protectedNav} favs={favs} toggleFav={toggleFav} />}
        {tela === 'suporte' && <TelaSuporte nav={protectedNav} />}
        {tela === 'convide' && <TelaConvideAmigos nav={protectedNav} user={user} />}
        {/* 'carteira' (Créditos de Indicação) foi FUNDIDA no Programa de
            Indicação: as duas liam o mesmo GET /usuarios/me/carteira e
            desenhavam o mesmo card de saldo — a segunda era só o extrato, que
            agora é um bloco expansível lá dentro. A rota continua viva de
            propósito: a notificação de bônus (TelaNotificacoes.js:143) e links
            antigos apontam pra 'carteira', e cair em tela branca seria pior. */}
        {tela === 'carteira' && <TelaConvideAmigos nav={protectedNav} user={user} />}
        {/* Portal antigo aposentado — 'portal_freteiro' agora é alias do hub
            único estilo Uber (HomeFreteiro). Links/pushes antigos não quebram. */}
        {tela === 'portal_freteiro' && <HomeFreteiro nav={protectedNav} user={user} data={data} />}
        {/* Painel do gestor agora e ABA do Programa de Indicacao — a rota
            antiga cai na mesma tela pra nao quebrar link salvo. */}
        {tela === 'admin_indicacoes' && <TelaConvideAmigos nav={protectedNav} user={user} />}
        {tela === 'dashboard_freteiro' && <TelaDashboardFreteiro nav={protectedNav} />}
        {tela === 'config_locador' && <TelaConfigLocador nav={protectedNav} />}
        {tela === 'freteiro_welcome' && <TelaFreteiroWelcome nav={protectedNav} user={user} />}
        {tela === 'freteiro_permissoes' && <TelaFreteiroPermissoes nav={protectedNav} />}
        {tela === 'verificacao' && <TelaVerificacao nav={protectedNav} data={data} />}
        {tela === 'documentos' && <TelaDocumentos nav={protectedNav} />}
        {/* /admin/devolucoes ('devolucoes_admin') estava no mapa de URLs sem tela */}
        {(tela === 'devolucoes' || tela === 'devolucoes_admin') && <TelaDevolucoesAdmin nav={protectedNav} />}
        {tela === 'precificacao' && <TelaMeusAnuncios nav={protectedNav} />}
        {tela === 'completar_lote' && <TelaCompletarLote nav={protectedNav} />}
        {tela === 'importar_catalogo' && <TelaImportarCatalogo nav={protectedNav} />}
        {tela === 'devolucoes_locador' && <TelaDevolucoesLocador nav={protectedNav} />}
        {tela === 'minhas_locacoes' && <TelaMinhasLocacoes nav={protectedNav} comoLocador={false} data={data} />}
        {tela === 'locacoes_recebidas' && <TelaMinhasLocacoes nav={protectedNav} comoLocador={true} data={data} />}
        {tela === 'config_admin' && <TelaConfigAdmin nav={protectedNav} data={data} />}
        {tela === 'audit_log_admin' && <TelaAuditLogAdmin nav={protectedNav} />}
        {tela === 'pedidos_locador' && <TelaPedidoLocador nav={protectedNav} data={data} />}
        {/* Central única da aba "Locações": Recebidos (locador) + Meus aluguéis
            (cliente) num lugar só — o papel é por PEDIDO, não por perfil. */}
        {tela === 'locacoes_central' && <TelaCentralLocacoes nav={protectedNav} data={data} />}
        {/* Rota do locador (estilo Uber Driver) — entrega pela locadora */}
        {tela === 'rota_locador' && <TelaRotaLocador nav={protectedNav} data={data} />}
        {tela === 'perguntas_locador' && <TelaPerguntasLocador nav={protectedNav} />}
        {tela === 'meus_pacotes' && <TelaMeusPacotes nav={protectedNav} />}
        {tela === 'detalhes_pacote' && <TelaDetalhesPacote nav={protectedNav} data={data} addCart={addCart} />}
        {/* Aceitou a oferta → cai no Freteiro App direto na vista de rota */}
        {tela === 'rota_freteiro' && <HomeFreteiro nav={protectedNav} user={user} data={{ view: 'rota' }} />}
        {tela === 'meus_strikes' && <TelaMeusStrikes nav={protectedNav} />}
        {tela === 'frete_disponivel' && <TelaFreteDisponivel nav={protectedNav} data={data} />}
        
        {/* Financeiro — adapta por perfil. historico_ganhos compartilha a mesma tela */}
        {tela === 'carteira_locador' && <TelaCarteiraLocador nav={protectedNav} />}
        {tela === 'financeiro' && <TelaFinanceiro nav={protectedNav} />}
        {tela === 'historico_ganhos' && <TelaFinanceiro nav={protectedNav} />}
        {tela === 'meus_veiculos' && <TelaMeusVeiculos nav={protectedNav} />}
        {tela === 'meus_niveis' && <TelaMeusNiveis nav={protectedNav} />}
        {tela === 'perfil_publico' && <TelaPerfilPublico nav={protectedNav} data={data} />}
        {tela === 'inadimplencias' && <TelaInadimplencias nav={protectedNav} />}
        {tela === 'calendario_locador' && <TelaCalendarioLocador nav={protectedNav} />}
        {tela === 'extensoes_pendentes' && <TelaExtensoesPendentes nav={protectedNav} />}
        {tela === 'meus_cupons' && <TelaMeusCupons nav={protectedNav} />}
        {tela === 'integracao_gestao' && <TelaIntegracaoGestao nav={protectedNav} />}
        {tela === 'entregas_locador' && <TelaEntregasLocador nav={protectedNav} />}
        {/* 'entregas_curso' abre o Freteiro App já na vista de ROTA — a
            corrida vive DENTRO do app (nada externaliza, como na Uber). */}
        {tela === 'entregas_curso' && <HomeFreteiro nav={protectedNav} user={user} data={{ view: 'rota' }} />}
        {tela === 'vistoria' && <TelaVistoria nav={protectedNav} />}
        {tela === 'config_loja' && <div className="min-h-screen bg-slate-50 p-4"><Card className="p-8 text-center"><Settings size={48} className="text-indigo-500 mx-auto mb-4" /><h2 className="text-xl font-bold mb-2">Configurações da Loja</h2><p className="text-slate-500">Em breve: raio de entrega e horários</p></Card></div>}
        {/* Categorias: mesmo padrão da faixa da home — quadrado só com o ícone 3D e o nome fora, embaixo (referência do dono, 25/09) */}
        {tela === 'categorias' && <div className="min-h-screen bg-slate-50 p-4"><h2 className="text-xl font-bold mb-4">Categorias</h2><div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-x-3 gap-y-5">{CATEGORIAS.map(c => <button key={c.id} type="button" onClick={() => protectedNav('busca', { catId: c.id })} className="flex flex-col items-center gap-2"><span className="w-full aspect-square rounded-[24px] bg-white border border-[#EEF1F4] flex items-center justify-center active:bg-orange-50"><img src={c.icone3d} alt="" width={64} height={64} draggable={false} decoding="async" className="w-[68%] h-[68%] object-contain select-none" /></span><span className="text-[12px] font-semibold text-slate-700 text-center leading-tight">{c.nome}</span></button>)}</div></div>}
        </Suspense>

        {/* FreteiroApp (painel_freteiro) tem abas PRÓPRIAS — BottomNav global fora */}
        {!fullscreen && tela !== 'detalhes' && tela !== 'detalhes_pacote' && <BottomNav tela={tela} nav={protectedNav} cart={cart} notifs={notifs} perfilAtivo={perfilAtivo} chatNaoLidas={chatNaoLidas} />}

        {/* Banner de MENSAGEM NOVA (estilo WhatsApp) — clicável, some em 6s.
            Aparece em qualquer tela fora do chat, com ou sem permissão de push. */}
        {bannerChat && (
          <button
            onClick={() => { setBannerChat(null); protectedNav('chat'); }}
            className="fixed left-1/2 -translate-x-1/2 z-[70] w-[calc(100%-24px)] max-w-md bg-[#12192a] text-white rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-3 text-left"
            style={{ top: 'calc(var(--header-h, 66px) + 8px)' }}
          >
            {/* Foto + nome de QUEM enviou (pedido do dono, 05/08) */}
            {bannerChat.foto ? (
              <img src={bannerChat.foto} alt=""
                className="w-9 h-9 rounded-full object-cover flex-shrink-0 border border-white/20" />
            ) : (
              <span className="w-9 h-9 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 text-sm font-extrabold">
                {(bannerChat.nome || 'M').trim().charAt(0).toUpperCase()}
              </span>
            )}
            <span className="flex-1 min-w-0">
              <span className="block text-[11px] font-bold uppercase tracking-wide text-white/60 truncate">
                {bannerChat.nome || 'Nova mensagem'}
              </span>
              <span className="block text-[13px] font-semibold truncate">{bannerChat.corpo}</span>
            </span>
            <span className="text-[11px] font-bold text-orange-400 flex-shrink-0">Abrir</span>
          </button>
        )}

        {/* FAB de online removido (06/2026): o toggle vive no Freteiro App
            (botão INICIAR/PARAR) e a barra de baixo leva pra lá de qualquer
            tela — o balão vermelho flutuante era ruído visual. */}
        {itemAdicionadoCarrinho && (
          <MiniDrawerCarrinho
            item={itemAdicionadoCarrinho}
            onFechar={() => setItemAdicionadoCarrinho(null)}
            onIrCarrinho={() => { setItemAdicionadoCarrinho(null); protectedNav('carrinho'); }}
          />
        )}
      </div>
    </NotificacoesPedidoProvider>
  );
};

// ─────────────────────────────────────────────────────────────────
// Banner "Nova versão disponível" — polling de /version.json a cada 5min.
// Quando a versão do servidor difere da embutida no bundle, mostra banner
// fixo no topo. Clicar dispara CLEAR_CACHE no SW + hard reload.
// ─────────────────────────────────────────────────────────────────

const App = () => (
  <ToastProvider>
    <ConfirmacaoProvider>
      <AuthProvider>
        <PerfilProvider>
          <OperandoComoProvider>
            <AppContent />
            <BuildHashFooter />
          </OperandoComoProvider>
        </PerfilProvider>
      </AuthProvider>
    </ConfirmacaoProvider>
  </ToastProvider>
);

export default App;
